import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgModule, LOCALE_ID, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeSv from '@angular/common/locales/sv';
import { registerLocaleData } from '@angular/common';
import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import { extModules } from './build-specifics';
import {
  TranslateModule,
  TranslateLoader,
  MissingTranslationHandlerParams,
  MissingTranslationHandler,
  TranslateService
} from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule, DateAdapter } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { StoreModule, Store } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import * as fromServices from '@services';
import { OidcSecurityService, PublicEventsService, EventTypes } from 'angular-auth-oidc-client'
import { AuthConfigModule } from './auth';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from '@core/containers/app/app.component';
import { reducers, metaReducers } from './reducers';
import { environment } from 'environments/environment';
import { CoreModule } from '@core/core.module';
import { JwtInterceptor } from '@core/interceptors';
import { httpErrorInterceptor } from '@core/interceptors';
import { RouterModule, Router, TitleStrategy } from '@angular/router';
import { AccountStoreModule } from '@appStore/account';
import { AppStoreModule } from '@appStore/app';
import { CustomersStoreModule } from '@appStore/customers';
import { ContractsStoreModule } from '@appStore/contracts';
import { ManagersStoreModule } from '@appStore/managers';
import { MeteringPointsModule } from '@metering/metering-points.module';
import { SxCookieService } from '@services';
import { ConfigurationModule } from '@appStore/configuration';
import * as fromConfiguration from '@appStore/configuration';
import { ApplicationConfiguration } from '@model';
import { MaterialModule } from '@material';
import { AnalyzeStoreModule } from '@appStore/analyze';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HelpStoreModule } from '@appStore/help';
import { ProductsStoreModule } from '@appStore/products';
import { UserType } from '@enums';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { PipesModule } from '@utils/pipes';
import { SxMonitoringService } from './services/sx-monitoring.service';
import { SxErrorHandler } from './services/sx-error-handler';
import { WebpackTranslateLoader } from './services/sx-webpack-translate-loader';
import { DirectivesModule } from '@utils/directives';
import { SwedishDateAdapter } from '@utils/SwedishDateAdapter';
import { TemplatePageTitleStrategy } from '@utils/TemplatePageTitleStrategy';
import { DragDropModule} from '@angular/cdk/drag-drop';
import { SelfServiceStoreModule } from '@appStore/selfservice';
import { getClientIdFromUserType }  from '@utils/sx-utilities';
import { LearningStoreModule } from '@appStore/learning';

/*
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
*/
export class SxMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    return '{' + params.key + '}';
  }
}

@NgModule({ bootstrap: [AppComponent],
    declarations: [], 
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: { strictStateImmutability: true, strictActionImmutability: true }
        }),
        extModules,
        EffectsModule.forRoot([]),
        CoreModule.forRoot(),
        AuthConfigModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        AccountStoreModule,
        AppStoreModule,
        CustomersStoreModule,
        ManagersStoreModule,
        ContractsStoreModule,
        MeteringPointsModule,
        ConfigurationModule,
        AnalyzeStoreModule,
        HelpStoreModule,
        ProductsStoreModule,
        SelfServiceStoreModule,
        LearningStoreModule,
        MatNativeDateModule,
        MatCardModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatInputModule,
        MaterialModule,
        MatSnackBarModule,
        PipesModule,
        DirectivesModule,
        DragDropModule,
        TranslateModule.forRoot({
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: SxMissingTranslationHandler
            },
            loader: {
                provide: TranslateLoader,
                useClass: WebpackTranslateLoader
            }
        })
    ], 
    providers: [
        { provide: LOCALE_ID, useValue: 'sv' },
        SxMonitoringService,
        {
            provide: ErrorHandler,
            useClass: SxErrorHandler
        },
        { provide: DateAdapter, useClass: SwedishDateAdapter },
        { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },
        OidcSecurityService,
      {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptor,
            multi: true
        },
        httpErrorInterceptor,
        ...fromServices.services,
        provideHttpClient(withInterceptorsFromDi())
    ] })

// Documentation https://github.com/damienbod/angular-auth-oidc-client
export class AppModule {
  constructor(
    private sxCookieService: SxCookieService,
    private configStore: Store<fromConfiguration.State>,
    private translate: TranslateService,
    private monitoringService: SxMonitoringService,
    private httpClient: HttpClient,
    private oidcEventsService: PublicEventsService,
  ) {
    translate.addLangs(['se', 'se-privat', 'se-foretag']);
    translate.setDefaultLang('se');
    translate.use('se');
    registerLocaleData(localeSv, 'sv');

    dayjs.locale('sv');
    
    let settingsUrl  = environment.apiEndpoint + '/appsettings';
    //console.log(`app-module\tloading appsettings from ${settingsUrl}`);
    this.httpClient.get<ApplicationConfiguration>(settingsUrl)
      .pipe(catchError(error => { console.warn(`app-module\tappsettings not loaded`, error); return error; }))
      .subscribe((config: ApplicationConfiguration) => {
        //console.log(`app-module\tsuccessfully loaded appsettings`, config);
      this.configStore.dispatch(new fromConfiguration.ApiConfigurationLoadSuccess(config));
    });

    let userType = this.sxCookieService.getCookie('SX_LOGGED_IN_AS') as UserType;
    //console.log(`app-module\tuserType`, userType);
    if (!!userType && userType.toString() !== '' && userType !== UserType.Unknown) {
      let clientId = getClientIdFromUserType(userType);
      this.configStore.dispatch(new fromConfiguration.SetActiveClient(clientId));
    }

    /*
    se-foretag && se-privat ar not used in the app, but we keep it for future use
    switch (userType) {
      case UserType.Company:
        translate.use('se-foretag');
        break;
      case UserType.Private:
        translate.use('se-privat');
        break;
      default:
        translate.use('se');
        break;
    }
    */
    const expectedAuthConfigsCount = 4; // brf, company, private, manager
    let configCounter = 0;
    this.oidcEventsService.registerForEvents()
      .pipe(
        filter(notification => notification.type === EventTypes.ConfigLoaded),
      )
      .subscribe(result => {
        //console.log(`app-module\toidc config loaded`, result?.value?.clientId);
        configCounter++;
        
        if (configCounter < expectedAuthConfigsCount) {
          return;
        }
        //console.log(`app-module\tconfigCounter`, configCounter);
        this.configStore.dispatch(new fromConfiguration.AuthSetupComplete());
      });
   
    this.configStore.select(fromConfiguration.getApiConfigurationLoaded)
      .pipe(
        filter(isLoaded => isLoaded),
        switchMap(() => this.configStore.select(fromConfiguration.getApiConfiguration)),
        filter(config => !!config),
        take(1),
      )
    .subscribe(apiConfig => {
      // Register AI, Google Analytics, Hotjar
      this.monitoringService.initService(
        apiConfig.applicationInsightsKey,
        apiConfig.googleAnalytics4Key,
        apiConfig.extendedApplicationsInsightsTracing
      );
      this.configStore.dispatch(new fromConfiguration.MenuConfigurationLoad());
    });
  }
}
